/**
 * /it/registro-delle-decisioni — decision-log concept page (Italian). Wedge.
 * Links to /it/trascrizione-audio bridge.
 */
import { MarketingShell } from './MarketingShell';

export function ItRegistroDecisioni() {
  return (
    <MarketingShell
      currentLocale="it"
      hreflangPath="/registro-delle-decisioni"
      hreflangLocales={['it']}
      documentTitle="Registro delle decisioni (decision log): perché e come · Draftlize"
      documentDescription="Un registro delle decisioni conserva il «perché» di ogni scelta di prodotto. La maggior parte marcisce: scritta una volta, mai riletta. Come farne uno vivo, con Draftlize."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guida · Registro delle decisioni</span>
          <h1 className="serif" style={{ fontSize: 'clamp(33px, 4.3vw, 52px)', lineHeight: 1.07, margin: '12px 0 24px' }}>
            Il registro delle decisioni che risponde<br />
            <em>a «perché l'abbiamo fatto?»</em>
          </h1>
          <p className="lede">
            Un registro delle decisioni (decision log) è il libro mastro che, per ogni scelta di prodotto, conserva <em>perché</em> si è deciso così. Mesi dopo, quando qualcuno riapre lo stesso dibattito, avere sotto mano contesto, opzioni e compromessi lo chiude in un minuto. Il problema: la maggior parte dei registri <em>marcisce</em> — scritti una volta, mai riletti, obsoleti in silenzio. Qui: come si fa e come tenerlo vivo.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Cosa registrare in un registro delle decisioni.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>La decisione</strong> — cosa si è deciso, in una frase.</li>
            <li><strong>Il contesto</strong> — perché serviva decidere adesso.</li>
            <li><strong>Opzioni e compromessi</strong> — cosa si è valutato e perché si è scartato il resto. <em>La parte più utile dopo</em>.</li>
            <li><strong>Responsabile e data</strong> — chi e quando.</li>
            <li><strong>Ambito</strong> — quali spec o decisioni tocca.</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            Questo formato coincide con l'ADR (registro delle decisioni di architettura). Molte decisioni nascono in riunione — estrarle prima con la <a href="/it/trascrizione-audio" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>trascrizione audio</a> è il punto di partenza.
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Perché i registri marciscono. <em>E come evitarlo.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Un registro delle decisioni in testo piatto inizia a divergere dalla realtà nel momento in cui lo scrivi. Una decisione successiva ne ribalta una precedente e il documento resta sulla vecchia. In Draftlize le decisioni sono «schede vive».
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Ogni decisione è una scheda indirizzabile</h3>
            <p>Una decisione = una scheda, con contesto, opzioni, motivo e responsabile, richiamabile per ID. Invece di perdersi in un documento, la puoi sempre indicare.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">La decisione ribaltata diventa «stale»</h3>
            <p>Quando una nuova decisione ne ribalta una vecchia, quella collegata — e le spec che vi poggiano — passano in «stale» in automatico. Non si rinegozia il già deciso.</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Il tuo agente IA lo legge e scrive</h3>
            <p>Il registro diventa contesto che Claude Code o Cursor leggono via MCP. L'agente scrive il prossimo passo sapendo «perché» si è deciso così.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          La maggior parte dei registri delle decisioni non li rilegge nessuno il giorno dopo. Così, sei mesi dopo, lo stesso dibattito riparte da zero.
        </blockquote>
        <cite>Conserva ogni decisione con il suo contesto e segnala cosa impatta quando cambia. Questo è Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Domande frequenti.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Registro delle decisioni o verbale di riunione?', a: 'Il verbale è «il resoconto della riunione»; il registro delle decisioni è «il libro mastro delle decisioni». Estrarre solo le decisioni dal verbale e accumularle è ciò che serve davvero dopo.' },
            { q: 'Chi dovrebbe tenerlo?', a: 'Di solito chi è responsabile della decisione (PM o lead). L\'essenziale è tenere tracciabile il «perché» di ogni decisione.' },
            { q: 'Quanto dettaglio?', a: 'Basta che siano chiari decisione, contesto e compromessi. Più della lunghezza conta poter accorgersi quando una decisione diventa obsoleta.' },
            { q: 'È lo stesso di un ADR?', a: 'L\'ADR è un tipo di registro delle decisioni tecnico. Draftlize tratta sia gli ADR sia le decisioni di prodotto come la stessa «scheda viva».' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Un registro delle decisioni che non marcisce <em>— gratis con $5.</em></h2>
        </div>
        <div className="trust-strip">
          <span>Registrati e ricevi $5</span>
          <span>Paghi solo ciò che usi</span>
          <span>Il saldo non scade mai</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Trasforma ogni decisione in una scheda con il suo contesto; se viene ribaltata si segna «stale» da sola, e il tuo agente IA la legge. Un registro delle decisioni vivo, con Draftlize.
          </p>
          <a className="btn-primary" href="/">Inizia gratis con $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
